/**
 * Role → navigation, ported from the design's PROFILES map.
 * Single source of truth: add a screen once here and it appears in the sidebar,
 * the mobile tab bar and the role's landing route.
 *
 * `icon` names resolve against components/Icons3D. A null `path` is a designed
 * destination that has no screen yet — rendered disabled rather than dropped, so
 * the sidebar matches the design exactly.
 */

import { WORKSPACES } from './desk';

/**
 * The sidebar's "Alpha Desk" section: one row per workspace, from the desk's
 * own config, so a workspace's key, icon and label are declared once.
 */
const deskNav = (...ids) => ['Alpha Desk', ids.map((id) => {
  const ws = WORKSPACES.find((w) => w.id === id);
  return [ws.key, ws.icon, ws.label];
})];

/** Screen id (from the design) → route path. */
export const ROUTES = {
  dashboard: '/dashboard',
  clients: '/clients',
  client360: '/clients/sharma',
  goals: '/goals',
  goal: '/goals/1',
  /* A client's trading accounts, their limits and trade ideas sized to them. */
  tradingGoals: '/trading-goals',
  /* How those trading accounts are doing: P&L, limits in use, payments and trades. */
  tradePortfolio: '/trade-portfolio',
  watchlist: '/watchlist',
  /* Every portfolio on the book — the list behind `portfolios` below. */
  portfolioList: '/portfolios',
  /* One portfolio in full, opened from the list or a client's Portfolio tab.
     No nav row of its own — a sample path, the way `model` and `kycClient`
     carry one. The key stays `portfolios` because the agent catalogue names
     the deep dive by it. */
  portfolios: '/portfolios/1',
  mandates: '/mandates',
  /* One model, opened from the register. No nav row of its own — it is reached
     by picking a row, the way a client is reached from the client list. */
  model: '/models/1',
  research: '/research',
  compare: '/compare',
  analyzer: '/analyzer',
  attribution: '/attribution',
  studio: '/studio',
  insights: '/insights',
  proposals: '/proposals',
  /* One saved proposal, opened from the list — no nav row of its own. */
  proposal: '/proposals/1',
  txn: '/transactions',
  cas: '/cas',
  empanel: '/empanelment',
  compliance: '/compliance',
  kyc: '/kyc',
  /* One client's KYC record, opened from the list. No nav row of its own — it
     is reached by starting or editing a row, the way a model is. */
  kycClient: '/kyc/1',
  amc: '/firm',
  funds: '/funds',
  fund: '/funds/ki-flexi',
  manager: '/managers/x',
  alpha: '/alpha',
  /* The stock alpha platform's desk: one route per screen, each its own row
     in the sidebar's "Stock Alpha Ranker" section (pages/StockAlpha.js). */
  stockAlphaToday: '/stock-alpha/today',
  stockAlphaRanks: '/stock-alpha/ranks',
  stockAlphaWhy: '/stock-alpha/why',
  stockAlphaBooks: '/stock-alpha/books',
  stockAlphaTheses: '/stock-alpha/theses',
  stockAlphaPaper: '/stock-alpha/paper',
  stockAlphaHealth: '/stock-alpha/health',
  stockAlphaFactors: '/stock-alpha/factors',
  stockAlphaResearch: '/stock-alpha/research',
  stockAlphaOptimiser: '/stock-alpha/optimiser',
  stockAlphaMine: '/stock-alpha/mine',
  /* A stock's research report in full, reached from Why this stock rather
     than from the sidebar — the desk's twelfth screen. */
  stockAlphaReport: '/stock-alpha/report',
  /* An individual's own home: their money, in one screen, with the assistant
     first — the landing of the `individual` seat. */
  individualHome: '/home',
  /* The Alpha Desk: six workspaces with tabs (config/desk.js), each a row in
     the sidebar. `/desk/{workspace}/{tab}`; the sample path is the first tab.
     The stock-alpha screens above now redirect into these. */
  deskIdeas: '/desk/ideas/today',
  deskStock: '/desk/stock/overview',
  deskResearch: '/desk/research/factors',
  deskBooks: '/desk/books/books',
  deskPortfolio: '/desk/portfolio/overview',
  deskTrade: '/desk/trade/orders',
  stocks: '/stocks',
  stock: '/stocks/RELIANCE.NSE',
  settings: '/settings',
  /* The KI staff console — app_admin's screens, on the employee seat.
     Namespaced under /admin so nothing here can collide with an advisor
     screen, and so the seat guard can reason about the whole console at once. */
  adminUsers: '/admin/users',
  employees: '/admin/employees',
  adminProspects: '/admin/prospects',
  support: '/admin/support',
  supportCall: '/admin/support/1',
  agentCosts: '/admin/agentic-costs',
  agentQuality: '/admin/agent-quality',
  /* System configuration — every tab app_admin carried behind its cog. Not
     `settings`, which is this app's own per-user preferences screen. */
  config: '/admin/configuration',
  /* The firm, its people and its clients' consent. Every seat has one, so it
     is universal rather than any one seat's destination. */
  team: '/team',
  profile: '/profile',
};

export const PROFILES = {
  mfd: {
    key: 'mfd',
    ava: 'VK',
    name: 'Venky Kopanathi',
    role: 'MFD · ARN-112233',
    landing: 'dashboard',
    ctx: 'Sharma Family · Client Deep Dive',
    /* The Alpha Desk first, as the transition notes draw the sidebar — Ideas,
       Stock and Research to find and test an idea; Stock models, Portfolio
       and Trade to hold and trade it (config/desk.js). */
    nav: [
      deskNav('ideas', 'stock', 'research', 'books', 'portfolio', 'trade'),
      ['Workspace', [
        ['dashboard', 'dashboard', 'Dashboard'],
        ['client360', 'client360', 'Client Deep Dive'],
        /* A proposal is client work, not research: it is drafted from a
           client's findings and sent to them, so it belongs beside the
           client screens rather than under Intelligence. */
        ['proposals', 'proposals', 'Proposals', '6'],
        /* Schemes and models being followed but not held — a working list, so
           it sits with the work rather than under Intelligence, and after the
           client screens because nothing on it is anyone's money yet. */
        ['watchlist', 'research', 'Watchlist'],
      ]],
      /* The money itself, as the individual's menu names it: every portfolio
         on the book (each opens in full), and the accounts — the goals' plans
         and the trading accounts, which are goals of the backend's Trading
         type; an account's rules, ideas and positions open from its card
         (IMPLIED.goals). */
      ['My money', [
        ['portfolioList', 'analyzer', 'Portfolios'],
        ['goals', 'goals', 'Accounts'],
      ]],
      ['Intelligence', [
        /* The model templates are research, not client work: they are studied
           and compared before anything is built from them. */
        ['mandates', 'mandates', 'Models'],
        ['research', 'research', 'Mutual Funds'],
        ['studio', 'studio', 'Analysis Studio', 'Run'],
        /* Last because it answers a narrower question than the screens above:
           which scheme in a category is likeliest to beat it. */
        ['alpha', 'fund', 'KI Alpha Ranker'],
        /* The stock master, and every name in it opens its own page — the same
           pair the fund-manager seat has had. */
        ['stocks', 'research', 'Stocks'],
      ]],
      ['Operations', [
        /* Whose KYC is outstanding, and the flow that clears it. */
        ['kyc', 'clients', 'KYC'],
        ['txn', 'txn', 'Transactions'],
        /* Reading a prospect's own statement is how a book grows: it is the
           only screen that sees the folios another distributor holds. */
        ['cas', 'txn', 'CAS Import'],
        ['empanel', 'empanel', 'AMC Empanelment'],
        [null, 'client360', 'Reports'],
        ['team', 'clients', 'Firm & team'],
        ['settings', 'settings', 'Settings'],
      ]],
    ],
  },
  pm: {
    key: 'pm',
    ava: 'RM',
    name: 'Rohit Mehta',
    role: 'Fund Manager (MF) · Flexi Cap, Small Cap',
    landing: 'funds',
    ctx: 'KI Flexi Cap Fund · Attribution',
    nav: [
      /* Cut to the three screens this seat is being used for. The rest —
         Strategy Book, Fund Research, Dealing, Attribution Reports, Settings —
         are built and routed; they are only absent from the nav, so restoring
         one is a line here. Note that a screen dropped from the nav also
         leaves this role's allowed set unless something below implies it. */
      /* The research side of the Alpha Desk; Portfolio and Trade are client
         work, which this seat does not do. */
      deskNav('ideas', 'stock', 'research', 'books'),
      ['My Funds', [
        ['funds', 'dashboard', 'Dashboard'],
        ['fund', 'analyzer', 'Fund Deep-Dive'],
        /* The desk's own working list — names being followed before anything is
           bought, the screens that found them, and the engine's ideas. Mostly
           stocks on this seat, where the MFD's is mostly schemes and models. */
        ['watchlist', 'research', 'Watchlist'],
        /* Saved proposals, each with its own Trade button: the fund manager
           places a plan's legs the same way the MFD and PMS desks do. */
        ['proposals', 'proposals', 'Proposals'],
      ]],
      ['Intelligence', [
        ['studio', 'studio', 'Fund Analysis', 'Run'],
        /* Same icon as the MFD seat's row for it — one screen, one mark. */
        ['alpha', 'fund', 'KI Alpha Ranker'],
        ['stocks', 'research', 'Stocks'],
      ]],
    ],
  },
  cio: {
    key: 'cio',
    ava: 'SK',
    name: 'S. Krishnamurthy',
    role: 'CIO · Keep Investing AMC',
    landing: 'amc',
    ctx: 'Firm-wide · 8 strategies',
    nav: [
      ['Firm', [
        ['amc', 'dashboard', 'Firm Overview'],
        ['fund', 'analyzer', 'Fund Deep-Dive'],
        ['mandates', 'mandates', 'Mandates & Risk', '184'],
        ['studio', 'studio', 'Analysis Studio', 'Run'],
      ]],
      ['Oversight', [
        ['research', 'research', 'Mutual Funds'],
        [null, 'empanel', 'Compliance'],
        [null, 'client360', 'Investor Reporting'],
      ]],
      ['Admin', [
        ['team', 'clients', 'Team & PMs'],
        ['settings', 'settings', 'Settings'],
      ]],
    ],
  },
  pms: {
    key: 'pms',
    ava: 'AN',
    name: 'Anjali Nair',
    role: 'PMS Relationship Mgr · 42 mandates',
    landing: 'dashboard',
    ctx: 'Private clients · 42 mandates',
    /**
     * The distributor's screens, seat for seat.
     *
     * A PMS desk does the same work on the same objects — clients, goals,
     * proposals, models, research — so a second, thinner arrangement of the
     * same screens only meant the PMS seat was missing things the MFD had.
     * What genuinely differs is not the screens but the constraints: bespoke
     * fees and mandates, a tighter IPS, and compliance as a standing concern
     * rather than a paragraph in a review — hence the extra row below.
     *
     * `mandates` is shared: the screen already reads the seat's role and calls
     * itself Mandates here and Models on the distributor's.
     */
    nav: [
      deskNav('ideas', 'stock', 'research', 'books', 'portfolio', 'trade'),
      ['Workspace', [
        ['dashboard', 'dashboard', 'Dashboard'],
        ['client360', 'client360', 'Client Deep Dive'],
        ['proposals', 'proposals', 'Proposals', '3'],
        ['watchlist', 'research', 'Watchlist'],
      ]],
      ['My money', [
        ['portfolioList', 'analyzer', 'Portfolios'],
        ['goals', 'goals', 'Accounts'],
      ]],
      ['Intelligence', [
        ['mandates', 'mandates', 'Mandates', '42'],
        ['research', 'research', 'Mutual Funds'],
        ['studio', 'studio', 'Analysis Studio', 'Run'],
        ['alpha', 'fund', 'KI Alpha Ranker'],
        ['stocks', 'research', 'Stocks'],
      ]],
      ['Operations', [
        /* The one row the distributor does not carry: a PMS mandate is
           discretionary, so breaches are the desk's own liability. */
        /* `mandates` icon: there is no compliance mark in Icons3D, and what
           this screen watches IS mandate adherence. Not `empanel`, which sits
           directly below it. */
        ['compliance', 'mandates', 'Compliance'],
        ['kyc', 'clients', 'KYC'],
        ['txn', 'txn', 'Transactions'],
        ['cas', 'txn', 'CAS Import'],
        ['empanel', 'empanel', 'AMC Empanelment'],
        [null, 'client360', 'Client Reporting'],
        ['team', 'clients', 'Firm & team'],
        ['settings', 'settings', 'Settings'],
      ]],
    ],
  },
  /**
   * An individual running their own money — a self-directed investor, a
   * high-frequency trader, an institutional individual. No firm and no
   * clients: the client scope is the person themselves (api/hooks/firm.js),
   * so every screen that works client by client shows their own portfolios,
   * goals, trading accounts, KYC and transactions. Mutual funds and stocks
   * both, with the full Alpha Desk.
   */
  individual: {
    key: 'individual',
    ava: 'ME',
    name: 'Individual investor',
    role: 'Individual · own account',
    landing: 'individualHome',
    ctx: 'My money · funds, stocks, trading accounts',
    nav: [
      ['My money', [
        ['individualHome', 'dashboard', 'Home'],
        ['portfolioList', 'analyzer', 'Portfolios'],
        ['goals', 'goals', 'Accounts'],
        ['proposals', 'proposals', 'Fund trades & proposals'],
        ['watchlist', 'research', 'Watchlist'],
      ]],
      deskNav('ideas', 'stock', 'research', 'books', 'portfolio', 'trade'),
      ['Research', [
        ['research', 'research', 'Mutual Funds'],
        ['alpha', 'fund', 'KI Alpha Ranker'],
        ['stocks', 'research', 'Stocks'],
        ['studio', 'studio', 'Analysis Studio', 'Run'],
      ]],
      ['Account', [
        ['txn', 'txn', 'Transactions'],
        ['cas', 'txn', 'CAS Import'],
        ['kyc', 'clients', 'KYC'],
        ['profile', 'settings', 'Profile'],
        ['settings', 'settings', 'Settings'],
      ]],
    ],
  },
  employee: {
    key: 'employee',
    ava: 'KI',
    name: 'KI Staff',
    role: 'Keep Investing · Internal',
    /* The console, not an advisor's dashboard: staff land on the platform's
       accounts, which is where supporting anyone starts. */
    landing: 'adminUsers',
    ctx: 'Keep Investing · all clients',
    /**
     * KI's own staff seat — what app_admin was.
     *
     * The distributor's screens, seat for seat, because an employee supporting
     * a client has to see exactly what that client's advisor sees. On top of
     * them sits the console app_admin carried: every client on the platform
     * rather than one advisor's book, the people records, the support desk,
     * what the agents cost, and the system configuration.
     *
     * Reachable only by a Keep Investing employee — see `useEmployeeSeat`. The
     * backend enforces the same thing independently: an ADMIN/EMPLOYEE token is
     * what lets a query answer for somebody else's client.
     */
    nav: [
      /* THE CONSOLE IS THE WORKSPACE. Staff are here to act on the platform —
         accounts, people, support, agents, configuration — not to run an
         advisor's book, so the advisor's Workspace (Dashboard, Client Deep
         Dive, Goal Planning, Proposals) is not in this seat's sidebar.

         A client is still one click away: every row under Users & clients
         opens that client's deep dive, goals and KYC (see IMPLIED.adminUsers),
         so an employee supporting a client sees what the advisor sees without
         the advisor's screens being destinations of their own. */
      ['Keep Investing', [
        /* Everyone on the platform, not one advisor's book. */
        ['adminUsers', 'clients', 'Users & clients'],
        ['employees', 'clients', 'Employees'],
        ['adminProspects', 'client360', 'Prospects'],
        ['support', 'call', 'Support console'],
        ['agentCosts', 'coin', 'Agentic costs'],
        ['agentQuality', 'spark', 'Agent quality'],
        ['config', 'settings', 'Configuration'],
      ]],
      /* The Alpha Desk after the console: the research side, and Portfolio —
         a client's money, reached the way Users & clients reaches it. */
      deskNav('ideas', 'stock', 'research', 'books', 'portfolio'),
      ['Intelligence', [
        ['mandates', 'mandates', 'Models'],
        ['research', 'research', 'Mutual Funds'],
        ['studio', 'studio', 'Analysis Studio', 'Run'],
        ['alpha', 'fund', 'KI Alpha Ranker'],
        ['stocks', 'research', 'Stocks'],
        /* Kept from the old Workspace: a research list, not client work. */
        ['watchlist', 'research', 'Watchlist'],
      ]],
      ['Operations', [
        ['kyc', 'clients', 'KYC'],
        ['txn', 'txn', 'Transactions'],
        ['empanel', 'empanel', 'AMC Empanelment'],
        ['team', 'clients', 'Firm & team'],
        ['settings', 'settings', 'Settings'],
      ]],
    ],
  },
};

/**
 * The seats an account can sign up as or be given at a firm.
 *
 * `employee` is deliberately absent: it is not a firm role anyone picks, it is
 * what a Keep Investing staff token confers. Everything that offers a choice of
 * seat reads this; everything that has to understand a seat reads SEAT_KEYS.
 */
export const PROFILE_KEYS = ['mfd', 'pm', 'cio', 'pms'];

/**
 * The seat of someone who invests their own money. Not a firm role either:
 * it is chosen at sign-up (or in preview) and needs no firm membership.
 */
export const INDIVIDUAL_SEAT = 'individual';

/** Every seat the app knows how to render, including the individual and staff ones. */
export const SEAT_KEYS = [...PROFILE_KEYS, INDIVIDUAL_SEAT, 'employee'];

/**
 * The organization whose staff get the console. The backend seeds it as the
 * default organization under exactly this name.
 */
export const KI_ORG_NAME = 'Keep Investing';

/**
 * Each seat key → the firm ROLE it stands for (the backend's
 * `EmployeeFunctionalRole`). The keys stay the app's own identifiers; anything a
 * user or the agent reads names the role.
 */
export const ROLE_OF_SEAT = {
  mfd: 'MFD', pm: 'PORTFOLIO_MANAGER', cio: 'CIO', pms: 'PMS_ADVISOR', individual: 'INDIVIDUAL', employee: 'EMPLOYEE',
};

/**
 * The kinds of firm a desk can belong to (the backend's `OrganizationType`),
 * with the label each is asked by. Named once because both places a firm is
 * created — the firm step of sign-up and Firm & team — offer the same choice.
 */
export const FIRM_TYPES = [
  ['MFD', 'Mutual fund distributor'],
  ['PMS', 'PMS provider'],
  ['AMC', 'Asset management company'],
  ['RIA', 'Registered investment adviser'],
];
export const FIRM_TYPE_LABEL = Object.fromEntries(FIRM_TYPES);

/**
 * The seat a session runs as when nothing else decides it.
 *
 * Used where a role is genuinely unknown: a first visit with nothing stored,
 * and a sign-in whose token carries no role claim and whose account never
 * recorded one at signup. That is every BoloDhan account signing in here with
 * the app's own credentials — a client, a trader, anyone without a firm role —
 * and what they should see is their own money: the individual seat. An
 * advisor whose firm role is still pending can switch seats on the Profile
 * page, where every desk stays previewable until a role is assigned. Named
 * once because the modules that read it would otherwise silently disagree.
 */
export const DEFAULT_ROLE = 'individual';
export const PROFILE_LABELS = {
  mfd: 'MFD', pm: 'PM', cio: 'CIO', pms: 'PMS / RM', individual: 'Individual', employee: 'KI Staff',
};

/** The same four seats spelled out, for forms and the profile page. */
export const PROFILE_TITLES = {
  mfd: 'Mutual Fund Distributor',
  pm: 'Fund Manager',
  cio: 'Chief Investment Officer',
  pms: 'PMS Relationship Manager',
  individual: 'Individual investor',
  employee: 'Keep Investing Staff',
};

/** Flat list of reachable destinations for a profile — drives the mobile tab bar. */
export const flatNav = (profile) =>
  profile.nav.flatMap(([, items]) => items).filter(([screen]) => screen);

/**
 * Which nav row owns a path.
 *
 * Prefix matching is wrong here: `/clients` (Clients) and `/clients/:id`
 * (Client Deep Dive) are separate destinations that share a prefix, as are `/funds`
 * and `/funds/:id`. Each screen declares exactly what it owns so only one row
 * ever highlights.
 */
const MATCHERS = {
  clients: (path) => path === '/clients',
  goals: (path) => path === '/goals',
  /* Exact, because /portfolios/:symbol is its own screen. */
  portfolioList: (path) => path === '/portfolios',
  /* One portfolio in full. No nav row of its own — the screen id exists so
     the route guard can resolve `/portfolios/:symbol` to something it can allow. */
  portfolios: (path) => /^\/portfolios\/[^/]+/.test(path),
  funds: (path) => path === '/funds',
  stocks: (path) => path === '/stocks',
  /* Exact, because /kyc/:id is its own screen: without this the prefix match
     would light the KYC row and resolve one client's record as the list. */
  kyc: (path) => path === '/kyc',
  /* Exact, because /proposals/:urlId is its own screen. */
  proposals: (path) => path === '/proposals',
  proposal: (path) => /^\/proposals\/[^/]+/.test(path),
  kycClient: (path) => /^\/kyc\/[^/]+/.test(path),
  client360: (path) => /^\/clients\/[^/]+/.test(path),
  fund: (path) => /^\/funds\/[^/]+/.test(path),
  manager: (path) => /^\/managers\/[^/]+/.test(path),
  stock: (path) => /^\/stocks\/[^/]+/.test(path),
  goal: (path) => /^\/goals\/[^/]+/.test(path),
  model: (path) => /^\/models\/[^/]+/.test(path),
  /* Exact, because /admin/support/:ticketId is the call's own screen — without
     this the prefix match lights the console row and resolves a live call as
     the queue. Same reason /admin/users is exact. */
  support: (path) => path === '/admin/support',
  supportCall: (path) => /^\/admin\/support\/[^/]+/.test(path),
  adminUsers: (path) => path === '/admin/users',
  individualHome: (path) => path === '/home',
  /* A workspace owns `/desk/<id>` and every tab under it. */
  deskIdeas: (path) => /^\/desk\/ideas(\/[^/]*)?$/.test(path),
  deskStock: (path) => /^\/desk\/stock(\/[^/]*)?$/.test(path),
  deskResearch: (path) => /^\/desk\/research(\/[^/]*)?$/.test(path),
  deskBooks: (path) => /^\/desk\/books(\/[^/]*)?$/.test(path),
  deskPortfolio: (path) => /^\/desk\/portfolio(\/[^/]*)?$/.test(path),
  deskTrade: (path) => /^\/desk\/trade(\/[^/]*)?$/.test(path),
};

/**
 * The screens a role may open.
 *
 * Its own nav defines them; a couple are reachable from within those screens
 * without being nav entries of their own, so they travel with the role that can
 * navigate to them.
 */
/* Every stock-alpha screen links to the others (the status strip, the keys,
   "open →" on Today), to a stock's own record, and back to the fund ranker. */
const STOCK_ALPHA_LINKS = [
  'stockAlphaToday',
  'stockAlphaRanks',
  'stockAlphaWhy',
  'stockAlphaBooks',
  'stockAlphaTheses',
  'stockAlphaPaper',
  'stockAlphaHealth',
  'stockAlphaFactors',
  'stockAlphaResearch',
  'stockAlphaOptimiser',
  'stockAlphaMine',
  'stockAlphaReport',
  'stocks', 'stock', 'alpha',
  /* The desk's research side, where every one of these screens now lands. */
  'deskIdeas', 'deskStock', 'deskResearch', 'deskBooks',
];

/* The desk's workspaces for holding and trading: each tab hands off to the
   client screens it will absorb, so a seat with the workspace can open them. */
const DESK_PORTFOLIO_LINKS = [
  'deskIdeas', 'deskStock', 'deskResearch', 'deskBooks', 'deskPortfolio',
  'client360', 'portfolioList', 'portfolios', 'goals', 'goal', 'proposals', 'proposal', 'analyzer', 'insights', 'watchlist',
];
const DESK_TRADE_LINKS = [
  'deskIdeas', 'deskStock', 'deskResearch', 'deskBooks', 'deskPortfolio', 'deskTrade',
  'tradingGoals', 'tradePortfolio', 'client360',
];

const IMPLIED = {
  stockAlphaToday: STOCK_ALPHA_LINKS,
  stockAlphaRanks: STOCK_ALPHA_LINKS,
  stockAlphaWhy: STOCK_ALPHA_LINKS,
  stockAlphaBooks: STOCK_ALPHA_LINKS,
  stockAlphaTheses: STOCK_ALPHA_LINKS,
  stockAlphaPaper: STOCK_ALPHA_LINKS,
  stockAlphaHealth: STOCK_ALPHA_LINKS,
  stockAlphaFactors: STOCK_ALPHA_LINKS,
  stockAlphaResearch: STOCK_ALPHA_LINKS,
  stockAlphaOptimiser: STOCK_ALPHA_LINKS,
  stockAlphaMine: STOCK_ALPHA_LINKS,
  stockAlphaReport: STOCK_ALPHA_LINKS,
  deskIdeas: [...STOCK_ALPHA_LINKS, 'watchlist', 'research'],
  deskStock: STOCK_ALPHA_LINKS,
  deskResearch: STOCK_ALPHA_LINKS,
  deskBooks: STOCK_ALPHA_LINKS,
  deskPortfolio: DESK_PORTFOLIO_LINKS,
  deskTrade: DESK_TRADE_LINKS,
  /* The individual's home links into everything that is theirs: the
     portfolios and the deep dive, goals and plans, proposals, the analyzer,
     the fund and stock pages the ideas open, and their own KYC record. */
  individualHome: [
    'portfolioList', 'portfolios', 'client360', 'goals', 'goal', 'proposals', 'proposal', 'analyzer', 'insights',
    'fund', 'stock', 'compare', 'kycClient', 'tradingGoals', 'tradePortfolio',
  ],
  clients: ['client360'],
  /* Every goal card opens its own plan, and that screen links back. A
     trading account's card opens its rules and ideas and its positions —
     the trading screens live under Goals, not in the menu. */
  goals: ['goal', 'tradingGoals', 'tradePortfolio'],
  goal: ['goals', 'clients', 'client360'],
  tradingGoals: ['goals', 'tradePortfolio'],
  tradePortfolio: ['goals', 'tradingGoals'],
  /* Every row opens that portfolio in full, and its client's deep dive. */
  portfolioList: ['portfolios', 'clients', 'client360', 'analyzer', 'proposals'],
  portfolios: ['analyzer', 'proposals'],
  client360: ['clients', 'analyzer', 'proposals', 'portfolios'],
  research: ['compare', 'fund'],
  /* A scheme's review names its managers, and a manager's book names its schemes. */
  fund: ['compare', 'funds', 'manager'],
  funds: ['fund', 'compare', 'research', 'manager'],
  manager: ['fund', 'funds'],
  /* Every row in the model register opens its own page. */
  mandates: ['fund', 'model'],
  model: ['mandates', 'fund'],
  amc: ['fund', 'mandates'],
  insights: ['proposals', 'studio'],
  /* The Studio hands its results to Deep Insights, so a seat that can run one
     can open the other — otherwise "Open results" bounces off the guard. */
  studio: ['insights'],
  analyzer: ['insights', 'proposals'],
  attribution: ['insights', 'fund'],
  /* Every ranked fund on the alpha screens opens its deep dive, and the
     universe builder searches the scheme master the research screen owns.
     NOT `funds`: nothing here links to the fund manager's own book, and
     implying it would hand that dashboard to any seat that gains this
     screen — the PM has it from its own nav either way. */
  alpha: ['fund', 'research'],
  /* The master's list rows and every peer, similar and diversifying name link
     into a stock's own record. */
  /* Every row of the proposals list opens that proposal's own page. */
  proposals: ['proposal', 'client360'],
  proposal: ['proposals', 'clients', 'client360'],
  /* A breach is always somebody's, so every row opens the client it belongs to. */
  compliance: ['clients', 'client360'],
  /* Every row on the KYC screen opens the client it belongs to, and starting
     or editing one opens that client's own KYC record. */
  kyc: ['clients', 'client360', 'kycClient'],
  kycClient: ['kyc', 'clients', 'client360'],
  stocks: ['stock'],
  stock: ['stocks'],
  /* A row opens the client deep dive — the same screen the account's own
     advisor sees, which is the point of opening it from here. And everything
     that deep dive links on to: the client's portfolios, the analyzer, and
     their proposals. The staff seat has none of these as menu items (its
     sidebar is the console), so without them here every link out of a
     client's page would bounce an employee back to the landing screen. */
  adminUsers: [
    'clients', 'client360', 'goals', 'goal', 'kyc', 'kycClient',
    'portfolios', 'analyzer', 'proposals', 'proposal',
  ],
  /* The console's screens all link back to one another through the cog and
     the console button in the top bar. */
  config: ['support', 'agentCosts', 'agentQuality', 'adminUsers'],
  support: ['supportCall', 'config', 'adminUsers', 'client360'],
  supportCall: ['support', 'adminUsers', 'client360'],
  agentCosts: ['config', 'agentQuality'],
  agentQuality: ['config', 'agentCosts'],
  employees: ['adminUsers', 'team'],
  adminProspects: ['adminUsers', 'clients', 'client360'],
};

/** The account's own pages: reachable from every seat, owned by none. `team`
    too — a PM with no nav row for it still has to be able to join a firm. */
const UNIVERSAL = ['profile', 'team'];

export function allowedScreens(roleKey) {
  const profile = PROFILES[roleKey];
  if (!profile) return new Set();
  const own = flatNav(profile).map(([screen]) => screen);
  const set = new Set([...own, ...UNIVERSAL]);
  own.forEach((s) => (IMPLIED[s] || []).forEach((extra) => set.add(extra)));
  return set;
}

/** Does this path belong to a screen the role may open? */
export function isPathAllowed(roleKey, pathname) {
  const allowed = allowedScreens(roleKey);
  const screen = Object.keys(ROUTES).find((s) => isScreenActive(s, pathname));
  /* An unmapped path (the index redirect, an unknown URL) is left to the router. */
  if (!screen) return true;
  return allowed.has(screen);
}

export const isScreenActive = (screen, pathname) => {
  if (!screen) return false;
  const matcher = MATCHERS[screen];
  if (matcher) return matcher(pathname);
  const to = ROUTES[screen];
  return Boolean(to) && pathname === to;
};

/**
 * The screens seats land on — one per profile. On these the assistant sits in
 * the page itself, centred under the greeting, rather than in the side column
 * it occupies everywhere else.
 */
export const HOME_PATHS = Array.from(new Set(
  Object.values(PROFILES).map((p) => ROUTES[p.landing]).filter(Boolean),
));

/** Is this path a seat's landing screen, where the assistant is in the page? */
export const isHomePath = (pathname) => HOME_PATHS.includes(pathname);
